import "../App.css";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logo from "../assets/Dark_L&W.png";
import { useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UploadModal from "../components/UploadModal";
import DownloadFile from "../components/DownloadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NewStatus from "../components/NewStatus";
import ErrorsDisplay from "../components/ErrorsDisplay";
import CopyToClipboard from "../components/CopyToClipboard";
import { DOWNLOAD_LICENSE_ENDPOINT } from "../config";

interface UploadCardProps {
  onOpenModal: () => void;
}

function UploadCard({ onOpenModal }: UploadCardProps) {
  return (
    <Card
      onClick={onOpenModal}
      sx={{
        height: "160px",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "all 0.3s ease",
        borderRadius: 2,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 15px rgba(43, 177, 50, 0.2)",
        },
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          textAlign: "center",
          py: 3,
          px: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <UploadFileIcon
          sx={{
            fontSize: 48,
            color: "#2BB132",
            mb: 2,
          }}
        />
        <Typography variant="subtitle1" fontWeight="600">
          Upload Request
        </Typography>
      </CardContent>
    </Card>
  );
}

interface DownloadCardProps {
  onDownload: () => void;
}

function DownloadCard({ onDownload }: DownloadCardProps) {
  return (
    <Card
      onClick={onDownload}
      sx={{
        height: "160px",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "all 0.3s ease",
        borderRadius: 2,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 15px rgba(33, 150, 243, 0.2)",
        },
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          textAlign: "center",
          py: 3,
          px: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <FileDownloadIcon
          sx={{
            fontSize: 48,
            color: "#2196F3",
            mb: 2,
          }}
        />
        <Typography variant="subtitle1" fontWeight="600">
          Download License
        </Typography>
      </CardContent>
    </Card>
  );
}

function Home() {
  const [openModal, setOpenModal] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<any>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseDownloadModal = () => {
    setOpenDownloadModal(false);
  };

  const handleFileUpload = (
    file: File,
    requestId: string,
    responseData: any
  ) => {
    console.log("File uploaded from modal:", file.name);
    console.log("Request ID received:", requestId);
    console.log("Response data:", responseData);

    // Store the uploaded file and request ID
    setUploadedFile(file);
    setRequestId(requestId);
    setError("");

    // Handle status based on response (similar to LicenseDetails pattern)
    if (responseData.status === "FAILED") {
      setLoading(false);
      setError(responseData.message || "Upload failed");
    } else if (
      responseData.status === "NEW" ||
      responseData.status === "PROCESSING"
    ) {
      setLoading(true);
    } else if (responseData.status === "SUCCESS") {
      setLoading(false);
      setData(responseData);
    }
  };

  const handleDownload = () => {
    console.log("Download clicked - opening download modal");
    setOpenDownloadModal(true);
  };

  const handleDirectDownload = async () => {
    if (!requestId) return;

    try {
      // Since backend sets proper headers, we can directly trigger download
      const downloadUrl = `${DOWNLOAD_LICENSE_ENDPOINT}/${requestId}`;

      // Check if the file exists first with a HEAD request
      const response = await fetch(downloadUrl, {
        method: "HEAD",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("File not found. Please try uploading again.");
        } else {
          throw new Error(`Download failed: ${response.statusText}`);
        }
      }

      // Direct download using hidden link approach
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", ""); // Let browser use filename from headers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("Download initiated for Request ID:", requestId);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(error instanceof Error ? error.message : "Download failed");
    }
  };

  // Success content component (similar to DataContent in LicenseDetails)
  const SuccessContent = () => {
    return (
      <Box px={2}>
        <Box border={"2px solid #e0e0e0"} borderRadius={2} p={2} my={2}>
          <Box display={"flex"} alignItems={"center"} gap={1} pb={1}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Upload Complete
            </Typography>
          </Box>
          <Stack my={1} gap={3} alignItems="center">
            <Box textAlign="center">
              <Typography variant="subtitle1" color={"grey"}>
                Request ID
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                justifyContent="center"
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 20,
                    fontFamily: "monospace",
                  }}
                >
                  {requestId}
                </Typography>
                {requestId && <CopyToClipboard text={requestId} />}
              </Box>
            </Box>
            <Box textAlign="center">
              <Typography variant="subtitle1" color={"grey"}>
                Status
              </Typography>
              <Typography
                sx={{ fontWeight: 500, fontSize: 20, color: "#2BB132" }}
              >
                {data?.status || "SUCCESS"}
              </Typography>
            </Box>
            {data?.feedback && (
              <Box textAlign="center">
                <Typography variant="subtitle1" color={"grey"}>
                  Message
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
                  {data.feedback}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Disclaimer */}
        <Box
          sx={{
            backgroundColor: "#FFF3CD",
            border: "1px solid #FDB813",
            borderRadius: 2,
            p: 2,
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "#856404" }}
          >
            📝 Please keep note of the Request ID for downloading the license
            file.
          </Typography>
        </Box>

        {/* Download Button - only show if status is SUCCESS */}
        {data?.status === "SUCCESS" && (
          <Box width={"100%"} my={2}>
            <Button
              variant="contained"
              startIcon={
                <FileDownloadIcon fontSize="small" sx={{ color: "white" }} />
              }
              fullWidth
              sx={{
                paddingY: 1.5,
                paddingX: 2,
                borderRadius: 2,
                backgroundColor: "#2196F3",
                "&:hover": {
                  backgroundColor: "#1976D2",
                },
              }}
              onClick={handleDirectDownload}
            >
              Download License File
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Content display logic (similar to LicenseDetails)
  const DisplayContent = () => {
    if (loading) {
      return <NewStatus />;
    } else if (error) {
      return <ErrorsDisplay error={error} smallScreen={isSmallScreen} />;
    } else if (data && requestId) {
      return <SuccessContent />;
    } else {
      // Show upload cards (initial state)
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "400px" }}>
            <UploadCard onOpenModal={handleOpenModal} />
          </Box>
          <Box sx={{ width: "100%", maxWidth: "400px" }}>
            <DownloadCard onDownload={handleDownload} />
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box
      width={"100%"}
      className="displayCenter"
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(170deg, #000000 35%, #3250ff)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Card
        sx={{
          p: isSmallScreen ? 2 : 3,
          borderRadius: 3,
          width: isSmallScreen ? "90%" : isMediumScreen ? "70%" : "50%",
          maxWidth: "900px",
          border: "0 solid #3250ff",
          borderWidth: 1.5,
          backgroundColor: "#F0F0F0",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        }}
      >
        {/* Logo Section */}
        <Paper
          className="displayCenter"
          elevation={5}
          sx={{
            borderRadius: 3,
            background: "linear-gradient(45deg, #000000, #3250ff)",
            mb: 2,
            py: 1.5,
          }}
        >
          <img src={logo} height={80} alt="LNW" />
        </Paper>

        <Box
          sx={{
            animation: "popupAnimation 1s ease forwards",
            textAlign: "center",
          }}
        >
          <DisplayContent />
        </Box>
      </Card>

      <UploadModal
        open={openModal}
        onClose={handleCloseModal}
        onUpload={handleFileUpload}
      />

      <DownloadFile
        open={openDownloadModal}
        onClose={handleCloseDownloadModal}
      />
    </Box>
  );
}

export default Home;
